import React, { useEffect, useRef, useState } from 'react';
import { FileImage, Keyboard, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { errMsg, todayIST } from '@/services/staffAttendanceApi';
import { ewbDocsApi, ewbInwardApi } from '@/services/ewayBillApi';
import { ManualDcForm } from '@/components/ewayBill/ManualDcForm';
import { defaultUnit, InwardForm } from '@/components/ewayBill/InwardForm';
import { BLANK_ITEM, ManualItemsEditor } from '@/components/ewayBill/ManualItemsEditor';

const POLL_MS = 2500;
const POLL_MAX = 72;
const NO_PARTY = { legal_name: '', gstin: '', address1: '', address2: '', place: '', pincode: '', state: '' };
const blank = (inward) => ({
  doc_no: '', doc_date: todayIST(), items: [{ ...BLANK_ITEM }], notes: '',
  ...(inward ? { kind: 'supplier', document_type: 'Tax Invoice', from: { ...NO_PARTY }, to_unit_id: null, party_ref: null, party_match: null }
    : { kind: 'job_work', to: { ...NO_PARTY } }),
});
const fromDoc = (d, inward) => ({
  ...blank(inward), ...d, kind: d.kind || blank(inward).kind, items: d.items?.length ? d.items : [{ ...BLANK_ITEM }], doc_date: d.doc_date || todayIST(),
  ...(inward ? { from: { ...NO_PARTY, ...(d.from || {}) } } : { to: { ...NO_PARTY, ...(d.to || {}) } }),
});
const num = (v) => Number(String(v ?? '').replace(/,/g, '')) || 0;
const body = (f) => ({ ...f, items: f.items.map((i) => ({ ...i, qty: num(i.qty), rate: num(i.rate), taxable_value: num(i.taxable_value), gst_rate: num(i.gst_rate) })) });
const TEXT = {
  false: { what: 'the manual delivery challan', reading: 'AI is reading the DC…', read: 'AI read the DC', saved: 'Manual DC' },
  true: { what: 'the supplier bill or the job worker\'s DC', reading: 'AI is reading the bill…', read: 'AI read the bill', saved: 'Inward bill' },
};

const Preview = ({ p }) => (
  <div className="lg:sticky lg:top-0 rounded-lg border border-gray-200 bg-slate-50 overflow-hidden" data-testid="manual-preview">
    {!p ? <p className="py-10 text-center text-xs text-gray-500">No file — typed in by hand</p>
      : p.mime === 'application/pdf' ? <iframe title="Document" src={p.url} className="h-[380px] w-full" />
        : <img src={p.url} alt="Document" className="max-h-[380px] w-full object-contain" />}
  </div>
);

/** Manual DC / inward bill: upload a photo / PDF → the AI reads it (background job, polled) → user checks and edits → save. */
export const ManualDcPanel = ({ initial, states, units, inward = false, onSaved, onCancel }) => {
  const t = TEXT[inward];
  const ref = useRef(null);
  const [f, setF] = useState(initial ? fromDoc(initial, inward) : null);
  const [uploadId, setUploadId] = useState(initial?.upload_id || null);
  const [preview, setPreview] = useState(null);
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    if (initial?.upload_id) ewbDocsApi.manualFileUrl(initial.upload_id).then((url) => alive.current && setPreview({ url, mime: initial.mime })).catch(() => {});
    return () => { alive.current = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const poll = (id, n = 0) => ewbDocsApi.manualStatus(id).then((u) => {
    if (!alive.current) return;
    if (u.status === 'reading' && n < POLL_MAX) { setTimeout(() => poll(id, n + 1), POLL_MS); return; }
    setReading(false);
    if (u.status === 'done') { setF(fromDoc(u.result, inward)); toast.success(`${t.read} — check every field before saving`); } else { setF(blank(inward)); toast.error(u.error || 'The AI took too long — type the details in', { duration: 8000 }); }
  }).catch((e) => { setReading(false); setF(blank(inward)); toast.error(errMsg(e, 'AI read failed')); });
  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPreview({ url: URL.createObjectURL(file), mime: file.type });
    setReading(true);
    (inward ? ewbInwardApi.extract(file) : ewbDocsApi.manualExtract(file)).then((u) => { setUploadId(u.id); poll(u.id); })
      .catch((err) => { setReading(false); toast.error(errMsg(err, 'Upload failed')); });
  };
  const save = () => {
    setBusy(true);
    const req = inward ? ewbInwardApi.save({ ...body(f), to_unit_id: f.to_unit_id || defaultUnit(units), upload_id: uploadId, edit_of: initial?.id || null })
      : ewbDocsApi.saveManual({ ...body(f), upload_id: uploadId, edit_of: initial?.doc_no || null });
    req.then((d) => { toast.success(`${t.saved} ${d.doc_no} saved`); onSaved(d); })
      .catch((e) => toast.error(errMsg(e, 'Save failed'), { duration: 8000 })).finally(() => setBusy(false));
  };
  if (reading) {
    return (
      <div className="py-12 text-center space-y-2" data-testid="manual-reading">
        <Sparkles className="w-10 h-10 mx-auto text-amber-500 animate-pulse" />
        <p className="font-semibold">{t.reading}</p><p className="text-sm text-gray-500">This takes about a minute. Keep this window open.</p>
        <Loader2 className="w-5 h-5 mx-auto animate-spin text-amber-600" />
      </div>
    );
  }
  if (!f) {
    return (
      <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white py-10 text-center space-y-3" data-testid="manual-upload">
        <FileImage className="w-10 h-10 mx-auto text-gray-400" />
        <p className="text-sm text-gray-600">Photo, scan or PDF of {t.what}</p>
        <input ref={ref} type="file" accept="image/*,application/pdf,.heic,.heif" className="hidden" onChange={pick} data-testid="manual-upload-input" />
        <div className="flex justify-center gap-2">
          <Button onClick={() => ref.current?.click()} className="bg-[#1A1F2E] hover:bg-black" data-testid="manual-upload-btn"><FileImage className="w-4 h-4 mr-1" />Choose file</Button>
          <Button variant="outline" onClick={() => setF(blank(inward))} data-testid="manual-type-in-btn"><Keyboard className="w-4 h-4 mr-1" />Type it in</Button>
        </div>
        <Button variant="ghost" onClick={onCancel} data-testid="manual-cancel-btn">Back</Button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="grid lg:grid-cols-5 gap-3">
        <div className="lg:col-span-2"><Preview p={preview} /></div>
        <div className="lg:col-span-3">{inward ? <InwardForm f={f} setF={setF} states={states} units={units} /> : <ManualDcForm f={f} setF={setF} states={states} />}</div>
      </div>
      <ManualItemsEditor items={f.items} onChange={(items) => setF({ ...f, items })} />
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onCancel} data-testid="manual-cancel-btn">Back</Button>
        <Button onClick={save} disabled={busy || !f.doc_no.trim() || !(inward ? f.from : f.to).gstin} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="manual-save-btn">
          {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save & continue</Button>
      </div>
    </div>
  );
};

export default ManualDcPanel;
